import posthog from 'posthog-js'

/**
 * Tracking funnel marketing (PostHog) lato webapp.
 *
 * Stesso progetto EU di landing + vetrina, così la persona si stitcha lungo
 * tutto il funnel: landing_view → assessment_start → assessment_complete →
 * register_complete → payment_success.
 *
 * Privacy: la webapp mostra dati clienti AML sensibili, quindi qui PostHog è
 * configurato in modo conservativo — niente autocapture (no scraping del DOM),
 * niente session recording, niente cattura automatica dei pageview. Si tracciano
 * SOLO gli eventi espliciti del funnel. `person_profiles: 'identified_only'`.
 *
 * Attribuzione: `cid` (id opaco del contatto dalla cold email) + `utm_*` +
 * `risk_band` arrivano nell'URL (cold email → landing → link di registrazione).
 * Vengono salvati in localStorage in first-touch (90 giorni) e registrati come
 * super-property, così ogni evento li porta con sé. Alla registrazione la
 * stessa attribuzione viene anche inviata e persistita sul record `User`
 * (campi cid, utm..., signupRiskBand) per collegare il percorso
 * pre-registrazione all'identità sulla piattaforma e alimentare i metadata Stripe.
 */

// La project key PostHog è PUBBLICA (esce comunque nel bundle): default
// hardcoded = stesso progetto di landing/vetrina, override via env se serve.
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY || 'phc_kXJQ5FJMbQZS3wMWu8pzEBRFiJBykgvX4BdfkJSsVAaf'
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com'

// Attivo solo nella build deployata (import.meta.env.PROD): in `vite dev` non
// inizializza, così lo sviluppo locale non inquina il progetto PostHog reale.
const ENABLED = import.meta.env.PROD && /^phc_[A-Za-z0-9]+$/.test(POSTHOG_KEY)
const ATTRIB_KEY = 'aegis_attrib'
const ATTRIB_TTL_MS = 90 * 24 * 60 * 60 * 1000 // 90 giorni

const ATTRIB_FIELDS = [
  'cid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'risk_band',
] as const

export type Attribution = Partial<Record<(typeof ATTRIB_FIELDS)[number], string>>

let initialized = false

type StoredAttribution = { data: Attribution; userId?: string }

// Lettura "grezza": include l'eventuale `userId` proprietario del first-touch
// (taggato da hydrateFromUser quando l'utente è loggato). Serve a rilevare un
// cambio utente sullo stesso browser.
function readStoredRaw(): StoredAttribution | null {
  try {
    const raw = localStorage.getItem(ATTRIB_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { ts?: number; data?: Attribution; userId?: string }
    if (parsed.ts && Date.now() - parsed.ts > ATTRIB_TTL_MS) {
      localStorage.removeItem(ATTRIB_KEY)
      return null
    }
    return { data: parsed.data ?? {}, userId: parsed.userId }
  } catch {
    return null
  }
}

function writeStored(data: Attribution, userId?: string): void {
  try {
    localStorage.setItem(ATTRIB_KEY, JSON.stringify({ ts: Date.now(), data, userId }))
  } catch {
    /* storage pieno/non disponibile: degradiamo silenziosamente */
  }
}

function readStored(): Attribution {
  return readStoredRaw()?.data ?? {}
}

function readFromUrl(): Attribution {
  const params = new URLSearchParams(window.location.search)
  const out: Attribution = {}
  for (const f of ATTRIB_FIELDS) {
    const v = params.get(f)
    if (v) out[f] = v
  }
  return out
}

/**
 * Restituisce l'attribuzione in first-touch: se già salvata, la mantiene (non
 * la sovrascrive con i parametri di un arrivo successivo); altrimenti la legge
 * dall'URL e la persiste.
 */
export function getAttribution(): Attribution {
  const stored = readStored()
  if (Object.keys(stored).length > 0) return stored
  const fromUrl = readFromUrl()
  // Cattura anonima (pre-login): nessun owner taggato.
  if (Object.keys(fromUrl).length > 0) writeStored(fromUrl)
  return fromUrl
}

/**
 * Consenso cookie analitici: stesso modello opt-in di vetrina/landing. Il
 * cookie-consent.js del sito scrive `aegis_cookie_consent=granted|denied` su
 * dominio `.aegis-aml.it` (+ localStorage), quindi qui sull'app lo leggiamo.
 * Chi arriva dal funnel ha già deciso sulla landing; chi va dritto sull'app
 * senza cookie resta non tracciato (nessun banner in-app in questa fase).
 */
export function hasAnalyticsConsent(): boolean {
  try {
    const m = document.cookie.match(/(?:^|;\s*)aegis_cookie_consent=([^;]+)/)
    if (m) return decodeURIComponent(m[1] ?? '') === 'granted'
    return localStorage.getItem('aegis_cookie_consent') === 'granted'
  } catch {
    return false
  }
}

/** Inizializza PostHog + identità/attribuzione. Idempotente. */
export function initAnalytics(): void {
  if (initialized || !ENABLED) return
  // GDPR: nessun init/tracking senza consenso esplicito (coerente col sito).
  if (!hasAnalyticsConsent()) return
  initialized = true
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
  })

  const attrib = getAttribution()
  if (attrib.cid) posthog.identify(attrib.cid)
  // Super-property: attaccate a TUTTI gli eventi successivi (anche register/payment).
  if (Object.keys(attrib).length > 0) posthog.register(attrib)
}

/**
 * "Rehydrate" del first-touch dal record User (esposto da /users/me), chiamato
 * dopo ogni fetch del current user (login, magic link, PAT). È AUTORITATIVO:
 * l'attribuzione del DB è la verità per l'utente loggato. Questo gestisce anche
 * il cambio utente sullo stesso browser SENZA logout (consumeLoginLink /
 * loginWithPat sostituiscono direttamente user/token):
 *   - utente CON attribuzione in DB → sovrascrive il locale e lo tagga col suo
 *     userId, poi identifica (cross-device + niente cid stale di un altro);
 *   - utente SENZA attribuzione → se il first-touch locale appartiene a un
 *     altro utente (o è anonimo), lo rimuove per non contaminarne il checkout.
 */
export function hydrateFromUser(
  userId: string | null | undefined,
  attribution: Attribution | null | undefined,
): void {
  if (!ENABLED || !initialized) return
  const owner = userId ?? undefined

  // 1) Cambio di utente LOGGATO sullo stesso browser senza logout
  //    (consumeLoginLink/loginWithPat sostituiscono user/token): azzera
  //    identità PostHog (distinct_id + super-property del vecchio utente) e il
  //    first-touch locale. NB: un first-touch anonimo (userId assente) NON è un
  //    cambio utente — è la transizione anonimo→identificato dello stesso
  //    soggetto, gestita da identify() sotto.
  const stored = readStoredRaw()
  if (stored?.userId && stored.userId !== owner) {
    try { posthog.reset() } catch { /* no-op */ }
    try { localStorage.removeItem(ATTRIB_KEY) } catch { /* no-op */ }
  }

  // 2) Attribuzione del DB = autoritativa per l'utente corrente.
  const clean: Attribution = {}
  if (attribution) {
    for (const f of ATTRIB_FIELDS) {
      const v = attribution[f]
      if (v) clean[f] = v
    }
  }
  if (Object.keys(clean).length > 0) {
    writeStored(clean, owner)
    try {
      if (clean.cid) posthog.identify(clean.cid)
      posthog.register(clean)
    } catch {
      /* no-op */
    }
    return
  }

  // 3) Utente corrente senza attribuzione: rimuovi un first-touch residuo che
  //    non è certamente suo (qui può essere solo un anonimo pre-login: il caso
  //    "altro utente taggato" è già stato gestito al punto 1). Oltre al
  //    localStorage va azzerata anche l'identità PostHog, perché initAnalytics()
  //    aveva registrato quel cid anonimo come super-property in memoria →
  //    altrimenti gli eventi successivi (es. checkout_start) lo porterebbero.
  const after = readStoredRaw()
  if (after && after.userId !== owner) {
    try { posthog.reset() } catch { /* no-op */ }
    try { localStorage.removeItem(ATTRIB_KEY) } catch { /* no-op */ }
  }
}

/** Emette un evento del funnel. No-op se PostHog non è attivo. */
export function track(event: string, props?: Record<string, unknown>): void {
  if (!ENABLED || !initialized) return
  try {
    posthog.capture(event, props)
  } catch {
    /* mai far fallire la UI per un errore di analytics */
  }
}

/**
 * Reset al logout (evita di mischiare persone su device condivisi). Pulisce
 * anche il first-touch locale: è sicuro perché al prossimo login viene
 * rehydratato dal DB (hydrateFromUser) per l'utente legittimo.
 */
export function resetAnalytics(): void {
  // Clear del first-touch sempre (anche in dev): non deve sopravvivere a un
  // cambio utente sullo stesso browser.
  try {
    localStorage.removeItem(ATTRIB_KEY)
  } catch {
    /* no-op */
  }
  if (!ENABLED || !initialized) return
  try {
    posthog.reset()
  } catch {
    /* no-op */
  }
}
